import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import axios from 'axios'

import { ORG_ENV, ORG_NAME } from '../../constants'

import { loadConfig } from '@ordernext/networking-stadium/stadium/actions'

import { fetchPromotions } from '../../actions/promotion'
import { menusItemsRequested, menusRequested } from '../../actions/menus'
import { fetchAttendants } from '../../actions/attendant'

import { getNetworkAvailable, getCurrentConfiguration } from '../../selectors/config'
import {
  getCurrentMenu,
  getCurrentStand,
  getRoundUpForCharityEnabled,
  getCharityOptionsEnabled,
  getCharityDescription,
  getCharityOptions,
  getCharityImage,
  getCharityName,
  getTextReceiptsEnabled,
  getEmailReceiptsEnabled,
  getIsKiosk,
  getPosQrReceiptsEnabled,
} from '../../selectors/menus'
import { getPrintingEnabled } from '../../selectors/printer'

import useSpinner from '../../context/hooks/useSpinner'

import StadiumRemote from '../../remote/stadiumRemote'

import {
  startDataLoad,
  setDataLoadInitiated,
  setBaseLoadInitiated,
  endBaseLoad,
  endDataLoad,
  setSections,
} from '../ActionCreators'
import { fetchTaxes } from '../../VNTaxes/ActionCreators'
import { getConfiguration, getGlobalConfiguration } from '../../VNConfigurations/ActionCreators'
import { setIsLocationPickerActive } from '../../VNLocationPicker/ActionCreators'
import { sendInitializeCFD } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { MODES } from '../../VNMode/Reducer'
import { setDeviceMode } from '../../VNMode/ActionCreators'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'

import createDataDogLog from '../../utils/datadog'
import { getAccessToken, getDeviceId, getDeviceSerial } from '../../utils/deviceData'

import { BRANDING_COLOR } from '../../constants'

import {
  getDataLoadRequested,
  getLastDataLoadInitiated,
  getLastDataLoadCompleted,
  getBaseLoadRequested,
  getShouldBaseLoadMenus,
  getLastBaseLoadInitiated,
  getLastBaseLoadCompleted,
  getSuccessCheck,
  getFailureCheck,
} from '../Selectors'

import { CONFIG_PRIME, PROMOTIONS_PRIME, MENUS_PRIME, MENUS_ITEMS_PRIME } from '../Reducer'
import { isE700Device } from '../../VNAndroidSDK/Utils'

import { bridge_getBuildInfo } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { invoiceNumberPrefix } from '../../utils/orderNumbers'
import { getDefaultTipEnabled } from '../../selectors/revenueCenter'
import { getQrReceiptsUrl } from '../../selectors/menus'

export const LOADING_DATA_TEXT = 'Loading data...'
const LOAD_TIMEOUT_MS = 20_000

export const VNDataLoadManager = () => {
  /*
    STILL TO DO:
    - Loading the latest version of the application.
    - Row and seat data (bringing useGetSeats hook here).
  */

  const dispatch = useDispatch()
  const { startSpinner, stopSpinner, showSpinner, spinnerText } = useSpinner()

  const currentStand = useSelector(getCurrentStand)
  const dataLoadRequested = useSelector(getDataLoadRequested)
  const lastDataLoadInitiated = useSelector(getLastDataLoadInitiated)
  const lastDataLoadCompleted = useSelector(getLastDataLoadCompleted)
  const baseLoadRequested = useSelector(getBaseLoadRequested)
  const shouldBaseLoadMenus = useSelector(getShouldBaseLoadMenus)
  const lastBaseLoadInitiated = useSelector(getLastBaseLoadInitiated)
  const lastBaseLoadCompleted = useSelector(getLastBaseLoadCompleted)
  const successCheck = useSelector(getSuccessCheck)
  const failureCheck = useSelector(getFailureCheck)

  const currentMenu = useSelector(getCurrentMenu)
  const printingEnabled = useSelector(getPrintingEnabled)
  const textReceiptsEnabled = useSelector(getTextReceiptsEnabled)
  const emailReceiptsEnabled = useSelector(getEmailReceiptsEnabled)
  const posQrCodeReceiptsEnabled = useSelector(getPosQrReceiptsEnabled)
  const roundUpForCharityEnabled = useSelector(getRoundUpForCharityEnabled)
  const charityOptionsEnabled = useSelector(getCharityOptionsEnabled)
  const charityDescription = useSelector(getCharityDescription)
  const charityOptions = useSelector(getCharityOptions)
  const charityImage = useSelector(getCharityImage)
  const charityName = useSelector(getCharityName)
  const deviceConfiguration = useSelector(getCurrentConfiguration)
  const isKiosk = useSelector(getIsKiosk)
  const androidSDKVersion = useSelector(getAndroidSDKVersion)
  const defaultTipEnabled = useSelector(getDefaultTipEnabled)
  const qrReceiptsUrl = useSelector(getQrReceiptsUrl)

  const [loadTimeout, setLoadTimeout] = useState(false)
  const [dataLoadStep, setDataLoadStep] = useState(0)
  const timeoutRef = useRef(null)

  const accessToken = getAccessToken()

  const configurationUuid = useMemo(() => deviceConfiguration?.uuid, [deviceConfiguration])

  const networkAvailable = useSelector(getNetworkAvailable)

  const getSections = useCallback(
    (standUuid) => {
      StadiumRemote.getSections(standUuid)
        .then((result) => {
          const returnedSections = result?.data?.map((section) => {
            return {
              name: section?.name,
              isSuite: section?.isSuite,
            }
          })

          if (returnedSections && returnedSections.length) {
            dispatch(setIsLocationPickerActive(true))
          } else if (networkAvailable) {
            dispatch(setIsLocationPickerActive(false))
          }

          dispatch(setSections(returnedSections))
        })
        .catch((err) => {
          dispatch(setSections([]))

          if (axios.isCancel(err)) return

          const errorMessage = err?.response?.data?.errorMessage ?? ''

          if (networkAvailable) {
            dispatch(setIsLocationPickerActive(false))
          }
          createDataDogLog('error', `There was an error in retrieving sections, ERROR: ${err ?? 'No Error Sent'}`, {
            errorMessage,
          })
        })
    },
    [dispatch, networkAvailable],
  )

  const terminateSyncAndProceed = useCallback(() => {
    dispatch(endDataLoad())
    dispatch(endBaseLoad())

    clearTimeout(timeoutRef?.current)
    setLoadTimeout(false)
    setDataLoadStep(0)

    stopSpinner()
  }, [dispatch, stopSpinner])

  const initializeCFD = useCallback(() => {
    let isCFD = false

    if (deviceConfiguration?.configuration?.deviceMode) {
      dispatch(setDeviceMode(deviceConfiguration?.configuration?.deviceMode))
      isCFD = deviceConfiguration?.configuration?.deviceMode === MODES.POS
    }

    if (isCFD) {
      const initializationValues = {
        title: currentStand?.name,
        color: BRANDING_COLOR,
        deviceId: getDeviceId(),
        serialNumber: getDeviceSerial(),
        appConfigurationDeviceId: configurationUuid,
        accessToken: accessToken,
        printingEnabled: printingEnabled,
        textReceiptsEnabled: textReceiptsEnabled,
        emailReceiptsEnabled: emailReceiptsEnabled,
        enablePosQrCodeReceipts: posQrCodeReceiptsEnabled,
        qrReceiptsUrl: qrReceiptsUrl,
        invoiceNumberPrefix: invoiceNumberPrefix(),
        charitySettings: {
          roundUpForCharityEnabled,
          charityOptionsEnabled,
          charityDescription,
          charityOptions,
          charityImage,
          charityName,
        },
        defaultTipEnabled,
      }

      initializationValues.menuImage = currentMenu?.images?.detail
      initializationValues.image = currentStand?.images?.detail

      sendInitializeCFD(initializationValues)
    }
  }, [
    dispatch,
    deviceConfiguration,
    currentStand,
    currentMenu,
    configurationUuid,
    accessToken,
    printingEnabled,
    textReceiptsEnabled,
    emailReceiptsEnabled,
    roundUpForCharityEnabled,
    charityOptionsEnabled,
    charityDescription,
    charityOptions,
    charityImage,
    charityName,
    defaultTipEnabled,
    posQrCodeReceiptsEnabled,
  ])

  // Terminate sync on network changes.
  useEffect(() => {
    // If the network goes out and the POS is in the middle of a load, then stop the spinner.
    if (!networkAvailable && (dataLoadRequested || baseLoadRequested)) terminateSyncAndProceed()
  }, [dispatch, terminateSyncAndProceed, dataLoadRequested, baseLoadRequested, networkAvailable])

  // Terminate sync on completion dates.
  useEffect(() => {
    if (lastDataLoadCompleted && showSpinner && spinnerText !== '') terminateSyncAndProceed()
  }, [dispatch, terminateSyncAndProceed, lastDataLoadCompleted, showSpinner, spinnerText])

  // Base load initiation.
  useEffect(() => {
    if (lastBaseLoadCompleted) return
    if (!baseLoadRequested) return

    dispatch(setBaseLoadInitiated())

    createDataDogLog('info', 'VNDataLoadManager: Base load started', {
      lastBaseLoadInitiated,
      lastBaseLoadCompleted,
      networkAvailable,
    })

    bridge_getBuildInfo(androidSDKVersion)

    dispatch(loadConfig({ redirect: shouldBaseLoadMenus, shouldReloadMenus: shouldBaseLoadMenus }))
    dispatch(fetchAttendants())

    dispatch(getConfiguration(ORG_NAME, ORG_ENV, 'public', 'runtime'))
    dispatch(getGlobalConfiguration(ORG_ENV, 'public', 'runtime'))

    // Get sections on a base load in case the stand has changed.
    if (shouldBaseLoadMenus) getSections(currentStand?.uuid)

    dispatch(endBaseLoad())

    // On E700 devices, kick off a data load after the base load for optimization.
    if (isE700Device() && !shouldBaseLoadMenus) dispatch(startDataLoad())
  }, [
    dispatch,
    getSections,
    currentStand,
    startSpinner,
    stopSpinner,
    baseLoadRequested,
    shouldBaseLoadMenus,
    lastBaseLoadInitiated,
    lastBaseLoadCompleted,
    networkAvailable,
    androidSDKVersion,
  ])

  // Data load initiation.
  useEffect(() => {
    // On E700 devices, the data load is kicked off at the end of the base load.
    if (isE700Device() && lastDataLoadInitiated) return

    if (lastDataLoadCompleted) return
    if (!dataLoadRequested) return

    // if offline, prevent data load calls
    if (!networkAvailable) return

    if (dataLoadStep === 0) {
      dispatch(setDataLoadInitiated())

      startSpinner('Loading configuration...')
      createDataDogLog('info', 'VNDataLoadManager: Data load started', {
        lastDataLoadInitiated,
        lastDataLoadCompleted,
        networkAvailable,
      })

      if (isKiosk) {
        timeoutRef.current = setTimeout(() => {
          setLoadTimeout(true)
        }, LOAD_TIMEOUT_MS)
      }

      dispatch(loadConfig({ redirect: false, shouldReloadMenus: false }))
      dispatch(getConfiguration(ORG_NAME, ORG_ENV, 'public', 'runtime'))
      dispatch(getGlobalConfiguration(ORG_ENV, 'public', 'runtime'))
      getSections(currentStand?.uuid)

      setDataLoadStep(1)
    }
  }, [
    dispatch,
    getSections,
    dataLoadRequested,
    startSpinner,
    currentStand,
    lastDataLoadInitiated,
    lastDataLoadCompleted,
    networkAvailable,
    isKiosk,
    dataLoadStep,
  ])

  // Data load step 1: Wait for config, then load promotions and taxes
  useEffect(() => {
    if (dataLoadStep !== 1) return
    if (successCheck % CONFIG_PRIME !== 0 && failureCheck % CONFIG_PRIME !== 0) return

    startSpinner('Loading promotions and taxes...')
    dispatch(fetchPromotions())
    dispatch(fetchTaxes())

    setDataLoadStep(2)
  }, [dataLoadStep, successCheck, failureCheck, dispatch, startSpinner])

  // Data load step 2: Wait for promotions and taxes, then load menus
  useEffect(() => {
    if (dataLoadStep !== 2) return
    if (successCheck % PROMOTIONS_PRIME !== 0 && failureCheck % PROMOTIONS_PRIME !== 0) return
    // TODO(mkramerl): Implement failure checks for taxes.
    // if (successCheck % TAXES_PRIME !== 0 && failureCheck % TAXES_PRIME !== 0) return

    startSpinner('Loading menus...')
    dispatch(menusRequested({ ignoreThrottle: true }))

    setDataLoadStep(3)
  }, [dataLoadStep, successCheck, failureCheck, dispatch, startSpinner])

  // Data load step 3: Wait for menus, then load menu items
  useEffect(() => {
    if (dataLoadStep !== 3) return
    if (successCheck % MENUS_PRIME !== 0 && failureCheck % MENUS_PRIME !== 0) return

    startSpinner('Loading menu items...')
    dispatch(menusItemsRequested())

    setDataLoadStep(4)
  }, [dataLoadStep, successCheck, failureCheck, dispatch, startSpinner])

  // Data load termination.
  useEffect(() => {
    if (dataLoadStep !== 4) return
    // TODO(mkramerl): Something weird going on with doing these checks in selectors. This was the
    // the only method that worked locally.
    if (successCheck % CONFIG_PRIME !== 0 && failureCheck % CONFIG_PRIME !== 0) return
    if (successCheck % PROMOTIONS_PRIME !== 0 && failureCheck % PROMOTIONS_PRIME !== 0) return
    if (successCheck % MENUS_PRIME !== 0 && failureCheck % MENUS_PRIME !== 0) return
    if (successCheck % MENUS_ITEMS_PRIME !== 0 && failureCheck % MENUS_ITEMS_PRIME !== 0) return

    // TODO(mkramerl): Implement failure checks for sections and taxes.
    // if (successCheck % SECTIONS_PRIME !== 0 && failureCheck % SECTIONS_PRIME !== 0) return
    // if (successCheck % TAXES_PRIME !== 0 && failureCheck % TAXES_PRIME !== 0) return

    terminateSyncAndProceed()
    initializeCFD()
  }, [dispatch, initializeCFD, terminateSyncAndProceed, successCheck, failureCheck, dataLoadStep])

  useEffect(() => {
    if (!loadTimeout) return

    clearTimeout(timeoutRef?.current)
    terminateSyncAndProceed()
  }, [loadTimeout, terminateSyncAndProceed])

  return <></>
}
